import React, { useState, useEffect, forwardRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClients, useSuppliers, useDestinations, useAirlines, useBankAccounts, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.min.css';

// Custom styles për react-select
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderWidth: '2px',
    borderColor: state.isFocused ? '#22c55e' : '#e5e7eb',
    borderRadius: '0.5rem',
    padding: '0.25rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(34, 197, 94, 0.2)' : 'none',
    '&:hover': { borderColor: '#22c55e' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#22c55e' : state.isFocused ? '#f0fdf4' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

// Custom input për DatePicker
const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-left bg-white hover:border-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all flex items-center justify-between"
    onClick={onClick}
    ref={ref}
  >
    <span className={value ? 'text-gray-800' : 'text-gray-400'}>
      {value || placeholder}
    </span>
    <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  </button>
));

export default function TicketForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: clients } = useClients();
  const { data: suppliers } = useSuppliers();
  const { data: destinations } = useDestinations();
  const { data: airlines } = useAirlines();
  const { data: bankAccounts } = useBankAccounts();
  const { invalidateAll } = useInvalidate();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    ticket_code: '',
    ticket_date: new Date(),
    client_id: '',
    client_name: '',
    supplier_id: '',
    destination_id: '',
    airline_id: '',
    passenger_name: '',
    passenger_phone: '',
    departure_date: null,
    return_date: null,
    fare: '',
    tax: '',
    cash: '',
    cash_account_id: '',
    notes: '',
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/tickets/${id}`).then(res => {
        const t = res.data;
        setForm({
          ticket_code: t.ticket_code || '',
          ticket_date: t.ticket_date ? new Date(t.ticket_date) : new Date(),
          client_id: t.client_id || '',
          client_name: t.client_name || t.client?.name || '',
          supplier_id: t.supplier_id || '',
          destination_id: t.destination_id || '',
          airline_id: t.airline_id || '',
          passenger_name: t.passenger_name || '',
          passenger_phone: t.passenger_phone || '',
          departure_date: t.departure_date ? new Date(t.departure_date) : null,
          return_date: t.return_date ? new Date(t.return_date) : null,
          fare: t.fare || '',
          tax: t.tax || '',
          cash: t.cash || '',
          cash_account_id: t.cash_account_id || '',
          notes: t.notes || '',
        });
      }).catch(() => toast.error('Gabim në ngarkim'));
    }
  }, [id, isEdit]);

  const clientOptions = clients?.map(c => ({
    value: c.id,
    label: `${c.name}${c.detyrim > 0 ? ` (Detyrim: €${c.detyrim.toFixed(2)})` : ''} - ${c.code}`,
    name: c.name,
  })) || [];

  const supplierOptions = suppliers?.map(s => ({ value: s.id, label: `${s.name} (${s.code})` })) || [];
  const destinationOptions = destinations?.map(d => ({ value: d.id, label: d.name })) || [];
  const airlineOptions = airlines?.map(a => ({ value: a.id, label: a.name })) || [];

  const total = (parseFloat(form.fare) || 0) + (parseFloat(form.tax) || 0);
  const cashAmount = parseFloat(form.cash) || 0;
  const remaining = total - cashAmount;

  const handleClientChange = (selected) => {
    setForm({ ...form, client_id: selected?.value || '', client_name: selected?.name || '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...form,
        ticket_date: form.ticket_date ? dayjs(form.ticket_date).format('YYYY-MM-DD') : null,
        departure_date: form.departure_date ? dayjs(form.departure_date).format('YYYY-MM-DD') : null,
        return_date: form.return_date ? dayjs(form.return_date).format('YYYY-MM-DD') : null,
      };
      
      if (cashAmount > 0 && !payload.cash_account_id) {
        toast.error('Zgjidhni llogarinë ku do shkojë pagesa!');
        setLoading(false);
        return;
      }

      if (isEdit) {
        await api.put(`/tickets/${id}`, payload);
        toast.success('Bileta u përditësua!');
      } else {
        await api.post('/tickets', payload);
        toast.success('Bileta u shtua me sukses!');
      }
      
      invalidateAll();
      navigate('/tickets');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim!');
    } finally {
      setLoading(false);
    }
  };

  const cashAccounts = bankAccounts?.filter(a => a.is_cash) || [];
  const bankOnlyAccounts = bankAccounts?.filter(a => !a.is_cash) || [];

  const selectedClient = clientOptions.find(o => o.value === parseInt(form.client_id)) || null;
  const selectedSupplier = supplierOptions.find(o => o.value === parseInt(form.supplier_id)) || null;
  const selectedDestination = destinationOptions.find(o => o.value === parseInt(form.destination_id)) || null;
  const selectedAirline = airlineOptions.find(o => o.value === parseInt(form.airline_id)) || null;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Custom CSS për datepicker modern */}
      <style>{`
        .react-datepicker {
          font-family: inherit !important;
          border: none !important;
          border-radius: 16px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          overflow: hidden;
        }
        .react-datepicker__header {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%) !important;
          border-bottom: none !important;
          padding: 20px !important;
        }
        .react-datepicker__current-month {
          color: white !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          margin-bottom: 10px !important;
        }
        .react-datepicker__day-names {
          margin-top: 10px !important;
        }
        .react-datepicker__day-name {
          color: rgba(255,255,255,0.9) !important;
          font-weight: 600 !important;
          width: 40px !important;
          margin: 2px !important;
        }
        .react-datepicker__month {
          margin: 0 !important;
          padding: 16px !important;
          background: white !important;
        }
        .react-datepicker__day {
          width: 40px !important;
          height: 40px !important;
          line-height: 40px !important;
          margin: 2px !important;
          border-radius: 50% !important;
          color: #374151 !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }
        .react-datepicker__day:hover {
          background: #dcfce7 !important;
          color: #16a34a !important;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%) !important;
          color: white !important;
          font-weight: 700 !important;
        }
        .react-datepicker__day--today {
          background: #f0fdf4 !important;
          color: #16a34a !important;
          font-weight: 700 !important;
          border: 2px solid #22c55e !important;
        }
        .react-datepicker__day--today.react-datepicker__day--selected {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%) !important;
          color: white !important;
          border: none !important;
        }
        .react-datepicker__day--outside-month {
          color: #d1d5db !important;
        }
        .react-datepicker__navigation {
          top: 20px !important;
        }
        .react-datepicker__navigation-icon::before {
          border-color: white !important;
          border-width: 2px 2px 0 0 !important;
        }
        .react-datepicker__navigation:hover *::before {
          border-color: rgba(255,255,255,0.7) !important;
        }
        .react-datepicker__triangle {
          display: none !important;
        }
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
        .react-datepicker__month-dropdown-container,
        .react-datepicker__year-dropdown-container {
          margin: 0 8px !important;
        }
        .react-datepicker__month-select,
        .react-datepicker__year-select {
          padding: 6px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          background: rgba(255,255,255,0.25) !important;
          color: white !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          font-size: 0.95rem !important;
        }
        .react-datepicker__month-select option,
        .react-datepicker__year-select option {
          color: #374151 !important;
          background: white !important;
        }
        .react-datepicker__today-button {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%) !important;
          border: none !important;
          color: white !important;
          font-weight: 600 !important;
          padding: 10px !important;
          border-radius: 0 0 16px 16px !important;
        }
      `}</style>

      {/* Header */}
      <div className="bg-white rounded-t-xl shadow-sm border-b-4 border-primary-500 p-6 mb-0">
        <h1 className="text-2xl font-bold text-primary-600 flex items-center gap-3">
          <span className="text-3xl">✈️</span>
          {isEdit ? 'Ndrysho Biletën' : 'Regjistro Biletë të Re'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-b-xl">
        
        {/* SEKSIONI 1: Klienti */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-100">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 text-xl">👤</span>
            </div>
            <h2 className="text-lg font-semibold text-primary-600">Të dhënat e klientit</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                🔍 Kërko Klientin
              </label>
              <Select
                options={clientOptions}
                value={selectedClient}
                onChange={handleClientChange}
                placeholder="Shkruaj emrin ose kodin..."
                isClearable
                isSearchable
                styles={selectStyles}
                noOptionsMessage={() => "Nuk u gjet asnjë klient"}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Emri Klientit (manual)
              </label>
              <input
                type="text"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                placeholder="Ose shkruaj emrin manualisht"
              />
            </div>
          </div>
        </div>

        {/* SEKSIONI 2: Furnitori & Destinacioni */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 text-xl">🏢</span>
            </div>
            <h2 className="text-lg font-semibold text-primary-600">Furnitori & Destinacioni</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Furnitori <span className="text-red-500">*</span>
              </label>
              <Select
                options={supplierOptions}
                value={selectedSupplier}
                onChange={(s) => setForm({ ...form, supplier_id: s?.value || '' })}
                placeholder="🔍 Kërko..."
                isClearable isSearchable styles={selectStyles}
                noOptionsMessage={() => "Nuk u gjet"}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Destinacioni <span className="text-red-500">*</span>
              </label>
              <Select
                options={destinationOptions}
                value={selectedDestination}
                onChange={(s) => setForm({ ...form, destination_id: s?.value || '' })}
                placeholder="🔍 Kërko..."
                isClearable isSearchable styles={selectStyles}
                noOptionsMessage={() => "Nuk u gjet"}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Kompania Ajrore <span className="text-red-500">*</span>
              </label>
              <Select
                options={airlineOptions}
                value={selectedAirline}
                onChange={(s) => setForm({ ...form, airline_id: s?.value || '' })}
                placeholder="🔍 Kërko..."
                isClearable isSearchable styles={selectStyles}
                noOptionsMessage={() => "Nuk u gjet"}
              />
            </div>
          </div>
        </div>

        {/* SEKSIONI 3: Bileta & Pasagjeri */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-100">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 text-xl">🎫</span>
            </div>
            <h2 className="text-lg font-semibold text-primary-600">Të dhënat e biletës dhe pasagjerit</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Kodi i Biletës <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.ticket_code}
                onChange={(e) => setForm({ ...form, ticket_code: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                📅 Data Biletës <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={form.ticket_date}
                onChange={(date) => setForm({ ...form, ticket_date: date })}
                dateFormat="dd/MM/yyyy"
                customInput={<CustomDateInput placeholder="Zgjidh datën..." />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                todayButton="📍 Sot"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Emri Pasagjerit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.passenger_name}
                onChange={(e) => setForm({ ...form, passenger_name: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                📞 Nr. Telefoni
              </label>
              <input
                type="text"
                value={form.passenger_phone}
                onChange={(e) => setForm({ ...form, passenger_phone: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                🛫 Data Nisjes <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={form.departure_date}
                onChange={(date) => setForm({ ...form, departure_date: date })}
                dateFormat="dd/MM/yyyy"
                customInput={<CustomDateInput placeholder="Zgjidh datën e nisjes..." />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                todayButton="📍 Sot"
                minDate={new Date()}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                🛬 Data Kthimit
              </label>
              <DatePicker
                selected={form.return_date}
                onChange={(date) => setForm({ ...form, return_date: date })}
                dateFormat="dd/MM/yyyy"
                customInput={<CustomDateInput placeholder="Zgjidh datën e kthimit..." />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                todayButton="📍 Sot"
                minDate={form.departure_date || new Date()}
              />
            </div>
          </div>
        </div>

        {/* SEKSIONI 4: Pagesa */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 text-xl">💳</span>
            </div>
            <h2 className="text-lg font-semibold text-primary-600">Informacionet e pagesës</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Tarifa (€) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                <input
                  type="number" step="0.01" value={form.fare}
                  onChange={(e) => setForm({ ...form, fare: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded-lg pl-8 pr-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all bg-white"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                TSC/Fitim (€) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                <input
                  type="number" step="0.01" value={form.tax}
                  onChange={(e) => setForm({ ...form, tax: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded-lg pl-8 pr-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all bg-white"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                Totali (€)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                <input
                  type="text" value={total.toFixed(2)}
                  className="w-full border-2 border-gray-200 rounded-lg pl-8 pr-4 py-3 bg-gray-100 font-bold text-gray-700"
                  readOnly
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary-600 mb-1">
                💵 Cash (pagoi tani)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                <input
                  type="number" step="0.01" value={form.cash}
                  onChange={(e) => setForm({ ...form, cash: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded-lg pl-8 pr-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all bg-white"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Zgjedhja e arkës/bankës */}
          {cashAmount > 0 && (
            <div className="mt-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
              <p className="font-semibold text-green-800 mb-3">
                💰 Ku do shkojë pagesa e €{cashAmount.toFixed(2)}?
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-bold text-gray-600 mb-2 uppercase">💵 Cash (Arka)</p>
                  <div className="space-y-2">
                    {cashAccounts.length === 0 && <p className="text-gray-500 text-sm italic">Nuk keni arka</p>}
                    {cashAccounts.map(acc => (
                      <label key={acc.id} className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${form.cash_account_id == acc.id ? 'border-green-500 bg-green-100 shadow-md' : 'border-gray-200 bg-white hover:border-green-300'}`}>
                        <input type="radio" name="cash_account" value={acc.id} checked={form.cash_account_id == acc.id} onChange={(e) => setForm({ ...form, cash_account_id: e.target.value })} className="w-4 h-4 text-green-600 mr-3" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{acc.name}</p>
                          <p className="text-sm text-gray-500">{acc.currency} • Balanca: {acc.currency === 'EUR' ? '€' : 'L'}{parseFloat(acc.balance).toFixed(2)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-600 mb-2 uppercase">🏦 Bankë</p>
                  <div className="space-y-2">
                    {bankOnlyAccounts.length === 0 && <p className="text-gray-500 text-sm italic">Nuk keni llogari</p>}
                    {bankOnlyAccounts.map(acc => (
                      <label key={acc.id} className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${form.cash_account_id == acc.id ? 'border-blue-500 bg-blue-100 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
                        <input type="radio" name="cash_account" value={acc.id} checked={form.cash_account_id == acc.id} onChange={(e) => setForm({ ...form, cash_account_id: e.target.value })} className="w-4 h-4 text-blue-600 mr-3" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{acc.name}</p>
                          <p className="text-sm text-gray-500">{acc.currency} • Balanca: {acc.currency === 'EUR' ? '€' : 'L'}{parseFloat(acc.balance).toFixed(2)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {cashAmount > 0 && remaining > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-300">
              <p className="text-yellow-800">⚠️ Mbetje: <strong>€{remaining.toFixed(2)}</strong> (detyrim i klientit)</p>
            </div>
          )}
          {cashAmount > 0 && remaining <= 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-300">
              <p className="text-green-800">✅ Klienti po paguan <strong>të gjithë totalin</strong></p>
            </div>
          )}
        </div>

        {/* SEKSIONI 5: Shënime */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-100">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 text-xl">📝</span>
            </div>
            <h2 className="text-lg font-semibold text-primary-600">Shënime</h2>
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
            rows="3"
            placeholder="Shënime shtesë..."
          />
        </div>

        {/* BUTONAT */}
        <div className="p-6 bg-white rounded-b-xl">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button type="button" onClick={() => navigate('/tickets')} className="w-full sm:w-auto px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all">
              Anulo
            </button>
            <button type="submit" disabled={loading} className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Duke ruajtur...</> : <>💾 {isEdit ? 'Përditëso' : 'Ruaj Biletën'}</>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}